import React, { useEffect, useState } from 'react';
import { Container, Alert, Card, Button, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { useAuth } from '../utils/AuthContext';

const PaymentSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get session ID from URL query params
        const queryParams = new URLSearchParams(location.search);
        const sessionId = queryParams.get('session_id');
        
        if (!sessionId) {
          setError('No session ID found');
          setLoading(false);
          return;
        }
        
        // Verify the session with our backend
        const response = await axios.get(`/api/verify-checkout/${sessionId}`);
        
        if (response.data.success) {
          setSession({
            id: sessionId,
            status: 'success',
            plan: response.data.plan
          });
          
          // Update local storage with new user data if needed
          if (currentUser) {
            const updatedUser = { ...currentUser, plan: response.data.plan, paymentStatus: 'active' };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        } else {
          setError('Payment verification failed. Please contact support.');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError('Error verifying payment. Your subscription may still be active - please check your profile.');
        setLoading(false);
      }
    };
    
    verifyPayment();
  }, [location, currentUser]);
  
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <h2>Processing your payment...</h2>
        <p>Please wait while we confirm your subscription.</p>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          {error}
        </Alert>
        <Button 
          variant="primary" 
          onClick={() => navigate('/subscription')}
        >
          Back to Subscriptions
        </Button>
      </Container>
    );
  }
  
  return (
    <Container className="mt-5">
      <Card className="text-center">
        <Card.Header as="h5" className="bg-success text-white">
          Payment Successful!
        </Card.Header>
        <Card.Body>
          <div className="text-success mb-3" style={{ fontSize: '4rem' }}>✓</div>
          <Card.Title className="mt-3">Thank you for your subscription!</Card.Title>
          {session?.plan && (
            <Card.Text className="text-muted">
              You are now on the <strong>{session.plan.charAt(0).toUpperCase() + session.plan.slice(1)}</strong> plan.
            </Card.Text>
          )}
          <Card.Text>
            Your account has been successfully upgraded. You now have access to additional features and higher usage limits.
          </Card.Text>
          <div className="d-flex justify-content-center gap-3">
            <Button 
              variant="primary" 
              onClick={() => navigate('/')}
            >
              Go to Dashboard
            </Button>
            <Button 
              variant="outline-primary" 
              onClick={() => navigate('/profile')}
            >
              View Subscription
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PaymentSuccess;
